// client/src/coms/views/chatview_parts/useChatMessages.js
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { apiFetchJson, safeObj } from './chatUtils';
import {
  applyTypingEvent,
  clearTypingFor,
  createTypingNotifier,
  describeSendError,
  makeClientNonce,
  pruneTyping,
  reconnectDelayMs,
  sendWithRetry,
} from './chatRealtime';

// Drafts are stored per account (u<id>) so switching accounts on a device never shows another account's
// unsent text. Older drafts without an account were written by whoever used this browser; the first
// account to open that channel adopts them and the unscoped copy is removed.
function legacyDraftStorageKey(serverKey, channelUiId, channelKind) {
  const sid = String(serverKey || '').trim();
  const cid = String(channelUiId || '').trim();
  const kind = String(channelKind || 'text').trim().toLowerCase() === 'voice' ? 'voice' : 'text';
  if (!sid || !cid) return '';
  return `voxemera:draft:${kind}:${sid}:${cid}`;
}

function draftStorageKey(serverKey, channelUiId, channelKind, userId) {
  const legacy = legacyDraftStorageKey(serverKey, channelUiId, channelKind);
  const uid = Number(userId) > 0 ? Math.floor(Number(userId)) : 0;
  if (!legacy || !uid) return '';
  return legacy.replace('voxemera:draft:', `voxemera:draft:u${uid}:`);
}

function readDraftFromStorage(serverKey, channelUiId, channelKind, userId) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return '';
    const k = draftStorageKey(serverKey, channelUiId, channelKind, userId);
    if (!k) return '';
    const own = window.localStorage.getItem(k);
    if (own != null) return String(own);
    const legacyKey = legacyDraftStorageKey(serverKey, channelUiId, channelKind);
    const legacy = legacyKey ? window.localStorage.getItem(legacyKey) : null;
    if (legacy == null) return '';
    window.localStorage.setItem(k, legacy);
    window.localStorage.removeItem(legacyKey);
    return String(legacy);
  } catch {
    return '';
  }
}

function writeDraftToStorage(serverKey, channelUiId, channelKind, value, userId) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const k = draftStorageKey(serverKey, channelUiId, channelKind, userId);
    if (!k) return;
    const text = String(value || '');
    if (text) window.localStorage.setItem(k, text);
    else window.localStorage.removeItem(k);
  } catch {}
}

// For tests (drafts per account and conversation).
export { draftStorageKey, readDraftFromStorage, writeDraftToStorage };


function isMobileKeyboardViewport() {
  try {
    if (typeof window === 'undefined') return false;
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return true;
    if (window.innerWidth && window.innerWidth <= 768) return true;
    const ua = window.navigator && window.navigator.userAgent ? String(window.navigator.userAgent) : '';
    return /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  } catch {
    return false;
  }
}

function normalizeChannelKind(value) {
  return String(value || '').trim().toLowerCase() === 'voice' ? 'voice' : 'text';
}

function parseTimestampMs(value) {
  if (!value) return 0;
  const raw = String(value).trim();
  if (!raw) return 0;

  const hasExplicitTimezone = /(?:Z|[+\-]\d{2}:\d{2})$/i.test(raw);

  if (hasExplicitTimezone) {
    const direct = Date.parse(raw);
    if (Number.isFinite(direct)) return direct;
  }

  const normalizedUtc = raw.includes(' ')
    ? `${raw.replace(' ', 'T')}Z`
    : raw.includes('T') && !hasExplicitTimezone
      ? `${raw}Z`
      : '';

  if (normalizedUtc) {
    const asUtc = Date.parse(normalizedUtc);
    if (Number.isFinite(asUtc)) return asUtc;
  }

  const normalizedLocal = raw.includes(' ') ? raw.replace(' ', 'T') : raw;
  const localish = Date.parse(normalizedLocal);
  if (Number.isFinite(localish)) return localish;

  const direct = Date.parse(raw);
  if (Number.isFinite(direct)) return direct;

  return 0;
}

function isExpiredMessage(message, ttlMinutes) {
  const ttl = Number(ttlMinutes) > 0 ? Number(ttlMinutes) : 0;
  if (!ttl) return false;

  const createdAtMs = parseTimestampMs(message && message.created_at);
  if (!createdAtMs) return false;

  return Date.now() >= createdAtMs + ttl * 60 * 1000;
}

function filterUnexpiredMessages(rows, ttlMinutes) {
  const arr = Array.isArray(rows) ? rows : [];
  const ttl = Number(ttlMinutes) > 0 ? Number(ttlMinutes) : 0;
  if (!ttl) return arr;
  return arr.filter((row) => !isExpiredMessage(row, ttl));
}

function normalizeUploadIds(values) {
  const arr = Array.isArray(values) ? values : values == null ? [] : [values];
  const out = [];
  const seen = new Set();

  for (const value of arr) {
    const n = Number(value);
    if (!Number.isFinite(n)) continue;
    const id = Math.floor(n);
    if (id <= 0) continue;
    const key = String(id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(id);
  }

  return out;
}

function normalizeMessageFont(value) {
  const raw = String(value == null ? '' : value).trim();
  if (!raw || raw === 'inherit') return 'inherit';
  if (raw === 'Arial') return 'Arial';
  if (raw === 'Georgia') return 'Georgia';
  if (raw === 'Courier New') return 'Courier New';
  if (raw === 'Comic Sans MS') return 'Comic Sans MS';
  return 'inherit';
}

function getMessageSortNumber(message) {
  const m = safeObj(message);
  const idNum = Number(m.id);
  if (Number.isFinite(idNum) && idNum > 0) return idNum;

  const createdAtMs = parseTimestampMs(m.created_at);
  if (createdAtMs > 0) return createdAtMs;

  const updatedAtMs = parseTimestampMs(m.updated_at);
  if (updatedAtMs > 0) return updatedAtMs;

  return 0;
}

function dedupeMessagesById(rows) {
  const arr = Array.isArray(rows) ? rows : [];
  const byId = new Map();
  const withoutId = [];

  for (const row of arr) {
    const msg = safeObj(row);
    const id = msg.id != null ? String(msg.id).trim() : '';
    if (!id) {
      withoutId.push(msg);
      continue;
    }
    byId.set(id, msg);
  }

  return [...withoutId, ...Array.from(byId.values())];
}

function sortMessagesChronologically(rows) {
  const arr = Array.isArray(rows) ? [...rows] : [];
  arr.sort((a, b) => {
    const av = getMessageSortNumber(a);
    const bv = getMessageSortNumber(b);
    if (av !== bv) return av - bv;

    const aid = String(a && a.id != null ? a.id : '');
    const bid = String(b && b.id != null ? b.id : '');
    if (aid < bid) return -1;
    if (aid > bid) return 1;
    return 0;
  });
  return arr;
}

function normalizeLoadedRows(rows, maxCount = 200) {
  const deduped = dedupeMessagesById(rows);
  const sorted = sortMessagesChronologically(deduped);
  if (sorted.length <= maxCount) return sorted;
  return sorted.slice(sorted.length - maxCount);
}

export default function useChatMessages({
  serverKey,
  channelUiId,
  channelDbId,
  channelKind,
  isMessageChannel,
  isLoggedIn,
  canType,
  inputRef,
  scrollerRef,
  messageTtlMinutes = 0,
  pauseInitialStream = false,
  onIncomingMessageUpdate,
  onIncomingMessageDelete,
  onIncomingReactionUpdate,
  myUserId = 0,
  // Split view: only the pane the user is working in takes keyboard focus.
  autoFocus = true,
  // Tabs: a function that puts the list back where the user left it; returns false to fall back to the bottom.
  restoreScrollRef = null,
  // The user wrote in this conversation: edited the draft to non-empty text (never a draft restored on opening)
  // or sent something. Used to keep the conversation's tab (workspace/engagement.js).
  onEngaged = null,
  // End-to-end encrypted channels: { blocked, encryptMessage(text) } (useChannelE2ee). Blocked means nothing is
  // sent; with encryptMessage only ciphertext is sent, never the text.
  e2eeRef = null,
}) {
  const [draft, setDraft] = useState('');
  const onEngagedRef = useRef(onEngaged);
  onEngagedRef.current = onEngaged;
  // 'idle' | 'connecting' | 'live' | 'reconnecting' | 'revoked'
  const [streamStatus, setStreamStatus] = useState('idle');
  const [typingState, setTypingState] = useState({});
  const [sendErr, setSendErr] = useState('');
  const [messagesRaw, setMessagesRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [sending, setSending] = useState(false);

  const [editingMessageId, setEditingMessageId] = useState('');
  const [editDraft, setEditDraft] = useState('');
  const [editBusyMessageId, setEditBusyMessageId] = useState('');
  const [deleteBusyMessageId, setDeleteBusyMessageId] = useState('');
  const [messageActionErr, setMessageActionErr] = useState('');
  const [hoveredMessageId, setHoveredMessageId] = useState('');

  const sseRef = useRef(null);
  const typingNotifierRef = useRef(null);
  const liveSeenIdsRef = useRef(new Set());
  const expiryTimersRef = useRef(new Map());
  const refreshBusyRef = useRef(false);
  const revealTimerRef = useRef(null);
  const bottomHoldTimerRef = useRef(null);
  const bottomHoldUntilRef = useRef(0);
  const channelLoadTokenRef = useRef(0);

  const stickToBottomRef = useRef(true);
  const [hideScrollbar, setHideScrollbar] = useState(true);

  const normalizedChannelKind = normalizeChannelKind(channelKind);
  const channelBasePath = normalizedChannelKind === 'voice' ? 'voice-channels' : 'text-channels';
  const ttlMinutes = Number(messageTtlMinutes) > 0 ? Number(messageTtlMinutes) : 0;

  // After a send finishes, return focus to this composer only if the user has not moved on (split view:
  // a slow send in one pane must not pull the caret out of the other pane).
  const refocusInput = useCallback(() => {
    try {
      const input = inputRef.current;
      if (!input) return;
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      const view = input.closest ? input.closest('.coms-view') : null;
      if (active && active !== document.body && active !== input && !(view && view.contains(active))) return;
      input.focus();
    } catch {}
  }, [inputRef]);

  const isNearBottom = useCallback((el, thresholdPx = 60) => {
    if (!el) return true;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    return remaining <= thresholdPx;
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [scrollerRef]);

  const hideScrollerDuringJump = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    try {
      el.style.visibility = 'hidden';
    } catch {}
  }, [scrollerRef]);

  const showScrollerAfterJump = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    try {
      el.style.visibility = '';
    } catch {}
  }, [scrollerRef]);

  const clearRevealTimer = useCallback(() => {
    try {
      if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    } catch {}
    revealTimerRef.current = null;
  }, []);

  const clearBottomHoldTimer = useCallback(() => {
    try {
      if (bottomHoldTimerRef.current) clearTimeout(bottomHoldTimerRef.current);
    } catch {}
    bottomHoldTimerRef.current = null;
  }, []);

  const holdBottomForContentSettle = useCallback(
    (durationMs = 1800) => {
      const duration = Math.max(0, Number(durationMs) || 0);
      bottomHoldUntilRef.current = Date.now() + duration;
      clearBottomHoldTimer();

      if (duration > 0) {
        bottomHoldTimerRef.current = setTimeout(() => {
          if (Date.now() >= bottomHoldUntilRef.current) {
            bottomHoldUntilRef.current = 0;
          }
          bottomHoldTimerRef.current = null;
        }, duration + 50);
      }
    },
    [clearBottomHoldTimer]
  );

  const snapToBottomBeforeReveal = useCallback((loadToken) => {
    clearRevealTimer();
    holdBottomForContentSettle(2600);

    const step = () => {
      if (channelLoadTokenRef.current !== loadToken) return;
      scrollToBottom();
    };

    step();

    requestAnimationFrame(() => {
      if (channelLoadTokenRef.current !== loadToken) return;
      step();

      requestAnimationFrame(() => {
        if (channelLoadTokenRef.current !== loadToken) return;
        step();
        showScrollerAfterJump();
      });
    });

    const delayedSteps = [50, 140, 320, 700, 1200, 2000, 2600];
    delayedSteps.forEach((delay) => {
      const timer = setTimeout(() => {
        if (channelLoadTokenRef.current !== loadToken) return;
        if (!stickToBottomRef.current && Date.now() > bottomHoldUntilRef.current) return;
        step();
      }, delay);

      revealTimerRef.current = timer;
    });
  }, [clearRevealTimer, holdBottomForContentSettle, scrollToBottom, showScrollerAfterJump]);

  // An explicit jump (search result, bookmark) wins over the first page's "settle at the bottom" hold.
  const releaseBottomHold = useCallback(() => {
    clearRevealTimer();
    clearBottomHoldTimer();
    bottomHoldUntilRef.current = 0;
    stickToBottomRef.current = false;
  }, [clearRevealTimer, clearBottomHoldTimer]);

  const maybeAutoScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const shouldHoldBottom = Date.now() <= bottomHoldUntilRef.current;
    if (stickToBottomRef.current || shouldHoldBottom) {
      scrollToBottom();
    }
  }, [scrollerRef, scrollToBottom]);

  const clearExpiryTimer = useCallback((messageId) => {
    const id = messageId != null ? String(messageId) : '';
    if (!id) return;
    try {
      const timers = expiryTimersRef.current;
      const h = timers.get(id);
      if (h) clearTimeout(h);
      timers.delete(id);
    } catch {}
  }, []);

  const resetAllExpiryTimers = useCallback(() => {
    try {
      const timers = expiryTimersRef.current;
      for (const h of Array.from(timers.values())) {
        clearTimeout(h);
      }
      timers.clear();
    } catch {}
  }, []);

  const removeExpiredMessagesNow = useCallback(() => {
    if (!ttlMinutes) return;

    const expiredIds = [];

    setMessagesRaw((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      if (!arr.length) return arr;

      const keep = [];
      for (const msg of arr) {
        const id = msg && msg.id != null ? String(msg.id) : '';
        if (isExpiredMessage(msg, ttlMinutes)) {
          if (id) expiredIds.push(id);
          continue;
        }
        keep.push(msg);
      }

      if (keep.length === arr.length) return arr;
      return keep;
    });

    if (!expiredIds.length) return;

    for (const id of expiredIds) {
      clearExpiryTimer(id);
      try {
        const seen = liveSeenIdsRef.current;
        if (seen && seen.delete) seen.delete(id);
      } catch {}
      setEditingMessageId((prev) => (prev === id ? '' : prev));
      setHoveredMessageId((prev) => (prev === id ? '' : prev));
    }
  }, [ttlMinutes, clearExpiryTimer]);

  const scheduleExpiryRemoval = useCallback(
    (message) => {
      if (!ttlMinutes) return;

      const id = message && message.id != null ? String(message.id) : '';
      if (!id) return;

      clearExpiryTimer(id);

      const createdAtMs = parseTimestampMs(message && message.created_at);
      if (!createdAtMs) return;

      const expiresAtMs = createdAtMs + ttlMinutes * 60 * 1000;
      const delay = expiresAtMs - Date.now();

      if (delay <= 0) {
        setMessagesRaw((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
          return arr.filter((x) => String(x && x.id) !== id);
        });
        try {
          const seen = liveSeenIdsRef.current;
          if (seen && seen.delete) seen.delete(id);
        } catch {}
        setEditingMessageId((prev) => (prev === id ? '' : prev));
        setHoveredMessageId((prev) => (prev === id ? '' : prev));
        return;
      }

      const h = setTimeout(() => {
        removeExpiredMessagesNow();
        clearExpiryTimer(id);
      }, delay);

      try {
        expiryTimersRef.current.set(id, h);
      } catch {}
    },
    [ttlMinutes, clearExpiryTimer, removeExpiredMessagesNow]
  );

  const syncRowsIntoState = useCallback(
    (rows) => {
      const normalizedRows = normalizeLoadedRows(rows, 200);
      const nextRows = filterUnexpiredMessages(normalizedRows, ttlMinutes);

      resetAllExpiryTimers();

      setMessagesRaw(nextRows);

      try {
        const seen = liveSeenIdsRef.current;
        if (seen && seen.clear) {
          seen.clear();
          for (const r of nextRows) {
            if (r && r.id != null) seen.add(String(r.id));
          }
        }
      } catch {}

      for (const r of nextRows) {
        scheduleExpiryRemoval(r);
      }

      setTimeout(() => maybeAutoScroll(), 0);
    },
    [ttlMinutes, resetAllExpiryTimers, scheduleExpiryRemoval, maybeAutoScroll]
  );

  const resetUiStateForChannelSwitch = useCallback(() => {
    clearRevealTimer();
    clearBottomHoldTimer();
    bottomHoldUntilRef.current = 0;
    // The draft is not cleared here: the per-channel draft loader below sets it for the new channel.
    setMessagesRaw([]);
    setLoadErr('');
    setEditingMessageId('');
    setEditDraft('');
    setMessageActionErr('');
    setHoveredMessageId('');
    resetAllExpiryTimers();
  }, [resetAllExpiryTimers, clearRevealTimer, clearBottomHoldTimer]);

  const applyIncomingCreate = useCallback(
    (m) => {
      const incoming = safeObj(m);
      if (incoming.channel_id == null) return;
      if (String(incoming.channel_id) !== String(channelDbId)) return;
      if (ttlMinutes && isExpiredMessage(incoming, ttlMinutes)) return;

      const incomingId = incoming.id != null ? String(incoming.id) : '';
      if (incomingId) {
        const seen = liveSeenIdsRef.current;
        if (seen && seen.has && seen.has(incomingId)) return;
        if (seen && seen.add) seen.add(incomingId);
      }

      setMessagesRaw((prev) => {
        const arr = Array.isArray(prev) ? [...prev, { ...incoming }] : [{ ...incoming }];
        return normalizeLoadedRows(arr, 200);
      });

      scheduleExpiryRemoval(incoming);
      setTimeout(() => maybeAutoScroll(), 0);
    },
    [channelDbId, ttlMinutes, scheduleExpiryRemoval, maybeAutoScroll]
  );

  const applyIncomingUpdateLike = useCallback(
    (m) => {
      const incoming = safeObj(m);
      if (incoming.channel_id == null) return;
      if (String(incoming.channel_id) !== String(channelDbId)) return;
      if (incoming.id == null) return;

      const incomingId = String(incoming.id);
      if (incomingId) {
        const seen = liveSeenIdsRef.current;
        if (seen && seen.add) seen.add(incomingId);
      }

      if (ttlMinutes && isExpiredMessage(incoming, ttlMinutes)) {
        clearExpiryTimer(incomingId);
        setMessagesRaw((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
          return arr.filter((x) => String(x && x.id) !== incomingId);
        });
        return;
      }

      setMessagesRaw((prev) => {
        const arr = Array.isArray(prev) ? [...prev] : [];
        const idx = arr.findIndex((x) => String(x && x.id) === incomingId);
        if (idx === -1) return arr;
        const next = [...arr];
        next[idx] = { ...safeObj(next[idx]), ...incoming };
        return normalizeLoadedRows(next, 200);
      });

      scheduleExpiryRemoval(incoming);

      if (typeof onIncomingMessageUpdate === 'function') {
        onIncomingMessageUpdate(incoming);
      }
    },
    [channelDbId, ttlMinutes, clearExpiryTimer, scheduleExpiryRemoval, onIncomingMessageUpdate]
  );

  const removeMessageById = useCallback(
    (messageId, channelIdMaybe) => {
      const id = messageId != null ? String(messageId) : '';
      if (!id) return;

      if (channelIdMaybe != null && String(channelIdMaybe) !== String(channelDbId)) return;

      clearExpiryTimer(id);

      try {
        const seen = liveSeenIdsRef.current;
        if (seen && seen.delete) seen.delete(id);
      } catch {}

      setMessagesRaw((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        return arr.filter((x) => String(x && x.id) !== id);
      });

      setEditingMessageId((prev) => (prev === id ? '' : prev));
      setEditDraft((prev) => (editingMessageId === id ? '' : prev));
      setHoveredMessageId((prev) => (prev === id ? '' : prev));
    },
    [channelDbId, editingMessageId, clearExpiryTimer]
  );

  const loadMessages = useCallback(
    async (activeDbChannelId, opts = {}) => {
      const silent = !!(opts && opts.silent);
      const loadToken = Date.now() + Math.random();

      channelLoadTokenRef.current = loadToken;

      if (!isMessageChannel || !activeDbChannelId) {
        setMessagesRaw([]);
        setLoadErr('');
        setLoading(false);
        showScrollerAfterJump();
        return;
      }

      if (!silent) {
        hideScrollerDuringJump();
        setLoading(true);
      }

      setLoadErr('');
      setMessageActionErr('');
      setEditingMessageId('');
      setEditDraft('');

      try {
        const data = await apiFetchJson(
          `/api/coms/${channelBasePath}/${encodeURIComponent(activeDbChannelId)}/messages?limit=200&_=${Date.now()}`,
          { method: 'GET' }
        );

        if (channelLoadTokenRef.current !== loadToken) return;

        const rows = data && Array.isArray(data.messages) ? data.messages : [];
        const restore = !silent && restoreScrollRef && typeof restoreScrollRef.current === 'function' ? restoreScrollRef.current : null;
        if (!silent && !restore) {
          holdBottomForContentSettle(2600);
        }
        syncRowsIntoState(rows);

        if (restore) {
          // Returning to a tab: reopen at the saved position once the rows are on screen.
          stickToBottomRef.current = false;
          bottomHoldUntilRef.current = 0;
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              if (channelLoadTokenRef.current !== loadToken) return;
              if (restore()) {
                setHideScrollbar(false);
                showScrollerAfterJump();
                return;
              }
              stickToBottomRef.current = true;
              setHideScrollbar(true);
              snapToBottomBeforeReveal(loadToken);
            })
          );
        } else if (!silent) {
          stickToBottomRef.current = true;
          setHideScrollbar(true);
          snapToBottomBeforeReveal(loadToken);
        }
      } catch (e) {
        if (channelLoadTokenRef.current !== loadToken) return;

        if (!silent) {
          setMessagesRaw([]);
          showScrollerAfterJump();
        }
        setLoadErr(e?.message || 'Failed to load messages');
        console.error('ChatView loadMessages failed:', e);
      } finally {
        if (channelLoadTokenRef.current === loadToken && !silent) {
          setLoading(false);
        }
      }
    },
    [
      isMessageChannel,
      channelBasePath,
      syncRowsIntoState,
      holdBottomForContentSettle,
      hideScrollerDuringJump,
      showScrollerAfterJump,
      snapToBottomBeforeReveal,
      restoreScrollRef,
    ]
  );

  const refreshMessagesSilently = useCallback(async () => {
    if (!isMessageChannel || !channelDbId) return;
    if (refreshBusyRef.current) return;

    refreshBusyRef.current = true;
    try {
      const data = await apiFetchJson(
        `/api/coms/${channelBasePath}/${encodeURIComponent(channelDbId)}/messages?limit=200&_=${Date.now()}`,
        { method: 'GET' }
      );
      const rows = data && Array.isArray(data.messages) ? data.messages : [];
      syncRowsIntoState(rows);
    } catch (e) {
      console.error('ChatView silent refresh failed:', e);
    } finally {
      refreshBusyRef.current = false;
    }
  }, [isMessageChannel, channelDbId, channelBasePath, syncRowsIntoState]);

  const send = useCallback(
    // Resolves true once the message was accepted, false otherwise (the error is shown by the composer).
    async ({ closeEmojiPicker, textOverride, uploadIds, font, keepDraft = false } = {}) => {
      if (!canType || sending) return false;

      const raw = textOverride != null ? String(textOverride || '') : String(draft || '');
      const text = raw.trim();
      const normalizedUploadIds = normalizeUploadIds(uploadIds);
      const normalizedFont = normalizeMessageFont(font);

      if (!text && !normalizedUploadIds.length) return false;
      if (!channelDbId) return false;
      const e2ee = e2eeRef && e2eeRef.current;
      if (e2ee && e2ee.blocked) {
        setSendErr('Sending is paused: see the encryption notice at the top of this channel.');
        return false;
      }
      if (e2ee && e2ee.encryptMessage && normalizedUploadIds.length) {
        setSendErr('Attachments cannot be sent in end-to-end encrypted channels yet.');
        return false;
      }
      if (onEngagedRef.current) onEngagedRef.current();

      setSending(true);
      setSendErr('');

      // One nonce for every retry of this send: the server stores the message at most once.
      const clientNonce = makeClientNonce();

      try {
        // Encrypted on this device first; if that fails nothing is sent.
        const encrypted = e2ee && e2ee.encryptMessage ? await e2ee.encryptMessage(text) : null;
        const sent = await sendWithRetry(() =>
          apiFetchJson(`/api/coms/${channelBasePath}/${encodeURIComponent(channelDbId)}/messages`, {
            method: 'POST',
            headers: encrypted ? { 'X-Vox-E2EE-Device': encrypted.deviceId } : undefined,
            body: JSON.stringify(
              encrypted
                ? { body: '', encryption: 'e2ee', ciphertext: encrypted.ciphertext, font: normalizedFont, client_nonce: clientNonce }
                : {
                    body: text,
                    upload_ids: normalizedUploadIds,
                    font: normalizedFont,
                    client_nonce: clientNonce,
                  }
            ),
          })
        );
        if (encrypted && sent && sent.message_id) encrypted.remember(sent.message_id);

        // Stickers are sent alongside whatever the user is typing, so keep their draft.
        if (!keepDraft) setDraft('');
        setSending(false);
        if (typingNotifierRef.current && !keepDraft) typingNotifierRef.current.stopped();

        // Without a live stream the sender would not see their own message until the next refresh.
        const es = sseRef.current;
        const streamLive = !!(es && es.readyState === 1);
        if (!streamLive && normalizedChannelKind !== 'voice' && sent && sent.message_id) {
          refreshMessagesSilently();
        }

        if (typeof closeEmojiPicker === 'function') {
          closeEmojiPicker();
        }

        stickToBottomRef.current = true;
        setHideScrollbar(true);
        setTimeout(() => {
          scrollToBottom();
          refocusInput();
        }, 0);

        if (normalizedChannelKind === 'voice') {
          refreshMessagesSilently();
        }
        return true;
      } catch (e) {
        console.error('ChatView send failed:', e);
        // The server's encryption status changed under us: refresh it so the notice and composer catch up.
        if (e && e.data && /^e2ee_/.test(String(e.data.code || ''))) {
          try {
            window.dispatchEvent(new CustomEvent('vox-server-e2ee-changed', { detail: {} }));
          } catch {}
        }
        setSendErr(describeSendError(e));
        setSending(false);
        refocusInput();
        return false;
      }
    },
    [
      canType,
      sending,
      draft,
      channelDbId,
      channelBasePath,
      refocusInput,
      scrollToBottom,
      normalizedChannelKind,
      refreshMessagesSilently,
      e2eeRef,
    ]
  );

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    function onScroll() {
      const atBottom = isNearBottom(el, 80);
      stickToBottomRef.current = atBottom;
      setHideScrollbar(atBottom);
    }

    const atBottomNow = isNearBottom(el, 80);
    stickToBottomRef.current = atBottomNow;
    setHideScrollbar(atBottomNow);

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      try {
        el.removeEventListener('scroll', onScroll);
      } catch {}
    };
  }, [scrollerRef, isNearBottom]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    let animationFrame = 0;

    const requestBottomCheck = () => {
      try {
        if (animationFrame) cancelAnimationFrame(animationFrame);
      } catch {}

      animationFrame = requestAnimationFrame(() => {
        animationFrame = 0;
        maybeAutoScroll();
      });
    };

    let resizeObserver = null;
    try {
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          requestBottomCheck();
        });
        resizeObserver.observe(el);
        if (el.firstElementChild) resizeObserver.observe(el.firstElementChild);
      }
    } catch {
      resizeObserver = null;
    }

    let mutationObserver = null;
    try {
      if (typeof MutationObserver !== 'undefined') {
        mutationObserver = new MutationObserver(() => {
          requestBottomCheck();
          if (resizeObserver && el.firstElementChild) {
            try {
              resizeObserver.observe(el.firstElementChild);
            } catch {}
          }
        });
        mutationObserver.observe(el, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['src', 'style', 'class'],
        });
      }
    } catch {
      mutationObserver = null;
    }

    const onMediaLoad = (event) => {
      const target = event && event.target ? event.target : null;
      const tag = target && target.tagName ? String(target.tagName).toLowerCase() : '';
      if (tag === 'img' || tag === 'video' || tag === 'iframe') {
        requestBottomCheck();
      }
    };

    el.addEventListener('load', onMediaLoad, true);
    el.addEventListener('loadedmetadata', onMediaLoad, true);

    requestBottomCheck();

    return () => {
      try {
        if (animationFrame) cancelAnimationFrame(animationFrame);
      } catch {}
      try {
        if (resizeObserver) resizeObserver.disconnect();
      } catch {}
      try {
        if (mutationObserver) mutationObserver.disconnect();
      } catch {}
      try {
        el.removeEventListener('load', onMediaLoad, true);
        el.removeEventListener('loadedmetadata', onMediaLoad, true);
      } catch {}
    };
  }, [scrollerRef, maybeAutoScroll]);

  useEffect(() => {
    if (!canType || !autoFocus) return;
    if (isMobileKeyboardViewport()) return;
    const t = setTimeout(() => {
      try {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      } catch {}
    }, 0);
    return () => clearTimeout(t);
  }, [canType, autoFocus, channelDbId, channelUiId, serverKey, normalizedChannelKind, inputRef]);

  useEffect(() => {
    resetUiStateForChannelSwitch();

    if (!serverKey) return;
    if (!isMessageChannel) return;

    if (!channelDbId) {
      setMessagesRaw([]);
      setLoadErr('');
      setLoading(false);
      showScrollerAfterJump();
      return;
    }

    loadMessages(channelDbId);
  }, [
    serverKey,
    channelUiId,
    channelDbId,
    normalizedChannelKind,
    isMessageChannel,
    resetUiStateForChannelSwitch,
    loadMessages,
    showScrollerAfterJump,
  ]);

  // The draft in state belongs to a channel only once the text loaded for it has been applied, so switching
  // channels never saves one channel's draft under another channel's key.
  const draftLoadRef = useRef({ key: '', value: '', applied: false });
  // Layout effect: the new channel's draft is in place before the switch is painted (no flash of the old one).
  useLayoutEffect(() => {
    if (!isMessageChannel || !serverKey || !channelUiId) {
      draftLoadRef.current = { key: '', value: '', applied: false };
      setDraft('');
      return;
    }
    const value = readDraftFromStorage(serverKey, channelUiId, normalizedChannelKind, myUserId);
    draftLoadRef.current = { key: draftStorageKey(serverKey, channelUiId, normalizedChannelKind, myUserId), value, applied: false };
    setDraft(value);
  }, [serverKey, channelUiId, normalizedChannelKind, isMessageChannel, myUserId]);

  useEffect(() => {
    if (!isMessageChannel || !serverKey || !channelUiId) return;
    const loaded = draftLoadRef.current;
    if (loaded.key !== draftStorageKey(serverKey, channelUiId, normalizedChannelKind, myUserId)) return;
    if (!loaded.applied) {
      if (draft === loaded.value) loaded.applied = true;
      return;
    }
    writeDraftToStorage(serverKey, channelUiId, normalizedChannelKind, draft, myUserId);
    // Only edits reach this point (the restored value is skipped above).
    if (String(draft || '').trim() && onEngagedRef.current) onEngagedRef.current();
  }, [serverKey, channelUiId, normalizedChannelKind, isMessageChannel, draft, myUserId]);

  useEffect(() => {
    try {
      if (sseRef.current) {
        try {
          sseRef.current.close();
        } catch {}
      }
    } catch {}
    sseRef.current = null;
    setTypingState({});

    if (pauseInitialStream || !isMessageChannel || !serverKey || !channelDbId || !isLoggedIn) {
      setStreamStatus('idle');
      return;
    }

    // EventSource reconnects by itself after network drops and resends Last-Event-ID, so the server
    // replays what was missed. It gives up after an HTTP error or a server-side close; then we
    // reconnect by hand with backoff, passing the last id we saw.
    let disposed = false;
    let es = null;
    let retryTimer = null;
    let attempt = 0;
    let lastEventId = '';
    const baseUrl = `/api/coms/${channelBasePath}/${encodeURIComponent(channelDbId)}/stream`;

    function safeParseJson(s) {
      try {
        if (!s) return null;
        return JSON.parse(s);
      } catch {
        return null;
      }
    }

    function track(ev) {
      if (ev && ev.lastEventId) lastEventId = ev.lastEventId;
    }

    function scheduleReconnect() {
      if (disposed || retryTimer) return;
      setStreamStatus('reconnecting');
      retryTimer = setTimeout(() => {
        retryTimer = null;
        connect();
      }, reconnectDelayMs(attempt++));
    }

    function reconnectNow() {
      if (disposed) return;
      if (es && es.readyState !== 2) return;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      connect();
    }

    function connect() {
      if (disposed) return;
      try {
        if (es) es.close();
      } catch {}
      const url = lastEventId ? `${baseUrl}?last_event_id=${encodeURIComponent(lastEventId)}` : baseUrl;
      try {
        try {
          es = new EventSource(url, { withCredentials: true });
        } catch {
          es = new EventSource(url);
        }
      } catch (e) {
        console.warn('ChatView SSE init failed:', e);
        scheduleReconnect();
        return;
      }
      sseRef.current = es;
      setStreamStatus((s) => (s === 'live' ? 'reconnecting' : s === 'reconnecting' ? s : 'connecting'));

      es.addEventListener('hello', (ev) => {
        const obj = safeParseJson(ev && ev.data ? ev.data : '') || {};
        attempt = 0;
        setStreamStatus('live');
        // resync: first connection (closes the gap after the history fetch) or missed events that
        // can no longer be replayed (server restart, long disconnect).
        if (obj.resync) {
          refreshMessagesSilently();
          try {
            window.dispatchEvent(new CustomEvent('vox-interaction-resync', { detail: { channelId: channelDbId } }));
          } catch {}
        }
      });

      es.addEventListener('message_create', (ev) => {
        track(ev);
        const obj = safeParseJson(ev && ev.data ? ev.data : '');
        if (!obj) return;
        setTypingState((prev) => clearTypingFor(prev, obj.user_id));
        applyIncomingCreate(obj);
      });

      es.addEventListener('message_update', (ev) => {
        track(ev);
        const obj = safeParseJson(ev && ev.data ? ev.data : '');
        if (obj) applyIncomingUpdateLike(obj);
      });

      es.addEventListener('message_delete', (ev) => {
        track(ev);
        const obj = safeParseJson(ev && ev.data ? ev.data : '');
        if (!obj) return;
        removeMessageById(obj.id, obj.channel_id);
        if (typeof onIncomingMessageDelete === 'function') onIncomingMessageDelete(obj);
      });

      es.addEventListener('reaction_update', (ev) => {
        track(ev);
        const obj = safeParseJson(ev && ev.data ? ev.data : '');
        if (obj && typeof onIncomingReactionUpdate === 'function') onIncomingReactionUpdate(obj);
      });

      // Channel Interactions: tell the cards on screen (interactions/interactionStore) which one changed; they
      // refetch it through the API, which shapes it for this member.
      es.addEventListener('interaction_update', (ev) => {
        track(ev);
        const obj = safeParseJson(ev && ev.data ? ev.data : '');
        if (!obj) return;
        try {
          window.dispatchEvent(new CustomEvent('vox-interaction-update', { detail: obj }));
        } catch {}
      });

      es.addEventListener('typing', (ev) => {
        const obj = safeParseJson(ev && ev.data ? ev.data : '');
        if (obj) setTypingState((prev) => applyTypingEvent(prev, obj, Date.now(), myUserId));
      });

      // Archive/restore/auto-archive changes for this channel (read-only bar, composer).
      es.addEventListener('channel_state', (ev) => {
        const obj = safeParseJson(ev && ev.data ? ev.data : '');
        try {
          window.dispatchEvent(new CustomEvent('vox-channel-state', { detail: { channelId: channelDbId, state: obj || {} } }));
          window.dispatchEvent(new CustomEvent('vox-channels-changed'));
        } catch {}
      });

      es.addEventListener('permissions_changed', (ev) => {
        const obj = safeParseJson(ev && ev.data ? ev.data : '');
        try {
          window.dispatchEvent(new CustomEvent('vox-permissions-changed', { detail: obj || {} }));
        } catch {}
      });

      es.addEventListener('access_revoked', () => {
        disposed = true;
        try {
          es.close();
        } catch {}
        setStreamStatus('revoked');
        // Lost access: drop what is on screen (and any tab for it) instead of keeping it readable.
        setMessagesRaw([]);
        setLoadErr('You no longer have access to this channel.');
        try {
          window.dispatchEvent(new CustomEvent('vox-channel-access-revoked', { detail: { serverKey, channelId: channelUiId } }));
        } catch {}
      });

      // The session behind this stream ended (account switched or signed out elsewhere in this browser).
      es.addEventListener('session_ended', (ev) => {
        const obj = safeParseJson(ev && ev.data ? ev.data : '') || {};
        if (obj.reason === 'session_renewed') return; // same account, new session id: EventSource reconnects
        disposed = true;
        try {
          es.close();
        } catch {}
        setStreamStatus('idle');
        try {
          window.dispatchEvent(new CustomEvent('vox-session-ended', { detail: { reason: obj.reason || 'session_ended' } }));
        } catch {}
      });

      es.onerror = () => {
        if (disposed) return;
        // readyState 0 = browser is retrying on its own; 2 = it gave up.
        if (es.readyState === 2) scheduleReconnect();
        else setStreamStatus('reconnecting');
      };
    }

    connect();

    const onOnline = () => reconnectNow();
    const onVisible = () => {
      if (document.visibilityState === 'visible') reconnectNow();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
      try {
        if (es) es.close();
      } catch {}
      sseRef.current = null;
    };
  }, [
    serverKey,
    channelUiId,
    channelDbId,
    channelBasePath,
    isMessageChannel,
    isLoggedIn,
    pauseInitialStream,
    myUserId,
    applyIncomingCreate,
    applyIncomingUpdateLike,
    removeMessageById,
    refreshMessagesSilently,
    onIncomingMessageDelete,
    onIncomingReactionUpdate,
  ]);

  // Expire typing indicators whose sender went quiet.
  useEffect(() => {
    if (!Object.keys(typingState).length) return undefined;
    const t = setInterval(() => setTypingState((prev) => pruneTyping(prev)), 1000);
    return () => clearInterval(t);
  }, [typingState]);

  // Outgoing typing pings for this channel (throttled here and on the server).
  useEffect(() => {
    if (!isMessageChannel || !channelDbId || !isLoggedIn) {
      typingNotifierRef.current = null;
      return undefined;
    }
    const url = `/api/coms/${channelBasePath}/${encodeURIComponent(channelDbId)}/typing`;
    const notifier = createTypingNotifier((body) => apiFetchJson(url, { method: 'POST', body: JSON.stringify(body) }));
    typingNotifierRef.current = notifier;
    return () => {
      notifier.stopped();
      if (typingNotifierRef.current === notifier) typingNotifierRef.current = null;
    };
  }, [isMessageChannel, channelDbId, channelBasePath, isLoggedIn]);

  const notifyTyping = useCallback((text) => {
    const n = typingNotifierRef.current;
    if (!n) return;
    if (String(text || '').trim()) n.typing();
    else n.stopped();
  }, []);

  useEffect(() => {
    if (!ttlMinutes || !isMessageChannel) return;

    const tick = setInterval(() => {
      removeExpiredMessagesNow();
    }, 1000);

    removeExpiredMessagesNow();

    return () => {
      clearInterval(tick);
    };
  }, [ttlMinutes, isMessageChannel, removeExpiredMessagesNow]);

  useEffect(() => {
    if (normalizedChannelKind !== 'voice') return;
    if (!isMessageChannel) return;
    if (!serverKey) return;
    if (!channelDbId) return;
    if (!isLoggedIn) return;

    // Safety net only: the stream replays missed events, so this no longer needs to be fast.
    const poll = setInterval(() => {
      refreshMessagesSilently();
    }, 15000);

    refreshMessagesSilently();

    return () => {
      clearInterval(poll);
    };
  }, [
    normalizedChannelKind,
    isMessageChannel,
    serverKey,
    channelDbId,
    isLoggedIn,
    refreshMessagesSilently,
  ]);

  useEffect(() => {
    return () => {
      clearRevealTimer();
      clearBottomHoldTimer();
      resetAllExpiryTimers();
      try {
        if (sseRef.current) {
          try {
            sseRef.current.close();
          } catch {}
        }
      } catch {}
      sseRef.current = null;
      showScrollerAfterJump();
    };
  }, [resetAllExpiryTimers, clearRevealTimer, clearBottomHoldTimer, showScrollerAfterJump]);

  return {
    draft,
    setDraft,
    streamStatus,
    typingState,
    sendErr,
    setSendErr,
    notifyTyping,
    messagesRaw,
    setMessagesRaw,
    loading,
    setLoading,
    loadErr,
    setLoadErr,
    sending,
    setSending,

    editingMessageId,
    setEditingMessageId,
    editDraft,
    setEditDraft,
    editBusyMessageId,
    setEditBusyMessageId,
    deleteBusyMessageId,
    setDeleteBusyMessageId,
    messageActionErr,
    setMessageActionErr,
    hoveredMessageId,
    setHoveredMessageId,

    hideScrollbar,
    setHideScrollbar,
    stickToBottomRef,
    releaseBottomHold,

    maybeAutoScroll,
    scrollToBottom,
    loadMessages,
    send,
    applyIncomingUpdateLike,
    removeMessageById,
  };
}